/**
 * lib/clarityScore.js — Phase 6C
 *
 * Client-side port of the authoritative backend Clarity Score computation
 * lives in /app/backend/lib/metrics.py :: compute_metrics(). The formula is
 * a weighted sum of 9 components that adds up to 100:
 *
 *   1. Transaction coverage      (20)   — n_txns / months_active
 *   2. Income visibility         (15)   — # income-months (excl. borrowing)
 *   3. Expense categorization    (10)   — % non-"Miscellaneous" expenses
 *   4. Savings consistency       (15)   — savings_rate tiers (>20/>10/>0)
 *   5. Recurring awareness       (10)   — # active recurring items
 *   6. Goal intentionality       (10)   — active + dated goals
 *   7. Runway stability          (10)   — runway months (>=6/>=3/>=1)
 *   8. Debt awareness             (5)   — any liability / emi ack / has_debt=false
 *   9. Investment visibility      (5)   — inv txn OR asset holding OR onb_savings > 0
 *
 * The score is "earned, not inflated" — it rewards awareness, organization,
 * consistency, and intentionality; NOT wealth or income.
 *
 * Stage gating (also mirrored from backend):
 *   • 'none'         — too little data; UI shows "Building your clarity"
 *   • 'early'        — enough for a soft estimate; muted styling
 *   • 'established'  — enough history to trust the number
 *
 * For the Figma Insights visual we bucket the 9 components into 4 dimensions:
 *   • Coverage & Data        = transaction_coverage + expense_categorization (30)
 *   • Cash flow              = income_visibility + savings_consistency + runway_stability (40)
 *   • Commitments & Debt     = recurring_awareness + debt_awareness (15)
 *   • Goals & Growth         = goal_intentionality + investment_visibility (15)
 *
 * Each bucket is displayed normalized to 0-100. The `overall` score remains the
 * true weighted 9-component sum so it always agrees with any backend response.
 *
 * ⚠️  If the backend formula in /app/backend/lib/metrics.py changes, this file
 *     MUST be updated in lockstep. Both files reference the same 9 components
 *     and tier thresholds by name so drift is easy to spot in diffs.
 */

// BUG-002 — one shared income rule (category set + loan-drawdown narrations).
import { isRealIncome } from './interimPolicy';

// ─── helpers ─────────────────────────────────────────────────────────
function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

function ymOf(row) {
  // Prefer txn_date (authoritative schema); tolerate legacy `date` alias.
  const raw = row?.txn_date || row?.date || '';
  return String(raw).slice(0, 7); // 'YYYY-MM'
}

// ─── main ────────────────────────────────────────────────────────────
/**
 * @param {Object} input
 * @param {Array}  input.txns        rows from `transactions` (must include txn_date, type, amount, category, source)
 * @param {Object} input.onboarding  row from `onboarding_profiles`
 * @param {Array}  input.holdings    rows from `wealth_holdings` (assets)
 * @param {Array}  input.liabilities rows from `liabilities`
 * @param {Array}  input.goals       rows from `goals`
 * @param {Array}  input.recurring   rows from `recurring_expenses`
 * @param {Object} input.profile     row from `profiles` (uses created_at for stage gating)
 * @param {number} [input.prevScore] optional last score, used for +/-7pt smoothing (matches backend)
 * @returns {{
 *   overall: number,
 *   stage: 'none'|'early'|'established',
 *   confidence: 'low'|'medium'|'high',
 *   band: string,
 *   deltaPts: number,
 *   components: Array<{key: string, label: string, value: number, max: number}>,
 *   buckets: Array<{label: string, score: number, color: string, raw: number, max: number}>,
 * }}
 */
export function computeClarityScore({
  txns = [],
  onboarding = null,
  holdings = [],
  liabilities = [],
  goals = [],
  recurring = [],
  profile = null,
  prevScore = null,
} = {}) {
  const onb = onboarding || {};

  // ---- Core money aggregates (Phase 56 sign convention: use magnitudes) ----
  const isExpense = (t) => t.type === 'expense';
  const isInvestment = (t) => t.type === 'investment';

  const income   = txns.filter(isRealIncome).reduce((s, t) => s + Math.abs(Number(t.amount) || 0), 0);
  const expenses = txns.filter(isExpense).reduce((s, t) => s + Math.abs(Number(t.amount) || 0), 0);
  const monthlyExpensesOnb = Number(onb.monthly_expenses || 0);
  const monthlyExpenses = monthlyExpensesOnb || expenses;
  const onbSavings = Number(onb.existing_investments || 0);

  // Investments are NOT subtracted from savings (they're a balance-sheet
  // conversion, not a cash outflow). This matches backend + Dashboard.
  const savings = income - expenses;
  const savingsRate = income > 0 ? (savings / income) * 100 : 0;
  const runway = monthlyExpenses > 0 ? onbSavings / monthlyExpenses : 0;

  // ---- Data-shape signals ----
  const n_txns = txns.length;
  const monthSet = new Set();
  const incomeMonthsSet = new Set();
  txns.forEach((t) => {
    const ym = ymOf(t);
    if (!ym) return;
    monthSet.add(ym);
    if (isRealIncome(t)) incomeMonthsSet.add(ym);
  });
  const months_active = monthSet.size;
  const has_income_txn = txns.some(isRealIncome);
  const has_imported_statement = txns.some((t) => t.source === 'pdf' || t.source === 'csv');
  const created_at = profile?.created_at ? new Date(profile.created_at) : null;
  const days_since_signup = created_at
    ? Math.max(0, Math.floor((Date.now() - created_at.getTime()) / 86400000))
    : 0;

  // ---- Stage gating (mirrors backend exactly) ----
  let stage = 'none';
  let confidence = 'low';
  if ((n_txns < 10 && !has_imported_statement && days_since_signup < 7) || n_txns < 3) {
    stage = 'none';
    confidence = 'low';
  } else if (n_txns >= 100 || months_active >= 2 || (has_imported_statement && n_txns >= 30)) {
    stage = 'established';
    confidence = 'high';
  } else {
    stage = 'early';
    confidence = 'medium';
  }

  // ---- Component 1: Transaction coverage (max 20) ----
  let c_coverage;
  if (n_txns >= 100 || months_active >= 3) c_coverage = 20;
  else if (n_txns >= 30) c_coverage = 15;
  else if (n_txns >= 10) c_coverage = 10;
  else if (n_txns >= 3)  c_coverage = 5;
  else                   c_coverage = 0;

  // ---- Component 2: Income visibility (max 15) ----
  let c_income;
  if (incomeMonthsSet.size >= 2) c_income = 15;
  else if (has_income_txn)       c_income = 8;
  else                           c_income = 0;

  // ---- Component 3: Expense categorization (max 10) ----
  const expenseTxns = txns.filter(isExpense);
  let c_cat = 0;
  if (expenseTxns.length > 0) {
    const categorized = expenseTxns.filter((t) => (t.category || 'Miscellaneous') !== 'Miscellaneous').length;
    c_cat = Math.round((categorized / expenseTxns.length) * 10);
  }

  // ---- Component 4: Savings consistency (max 15) ----
  let c_savings;
  if (savingsRate > 20)      c_savings = 15;
  else if (savingsRate > 10) c_savings = 10;
  else if (savingsRate > 0)  c_savings = 5;
  else                       c_savings = 0;

  // ---- Component 5: Recurring awareness (max 10) ----
  // Backend checks `not r.archived`. Real schema uses `active` boolean, and
  // the archived column does not exist — so we count anything not explicitly
  // archived AND still active, which is the intent.
  const recActive = (recurring || []).filter(
    (r) => !r.archived && r.active !== false,
  );
  let c_recurring;
  if (recActive.length >= 3)      c_recurring = 10;
  else if (recActive.length >= 1) c_recurring = 5;
  else                            c_recurring = 0;

  // ---- Component 6: Goal intentionality (max 10) ----
  const activeGoals = (goals || []).filter((g) => (g.status || 'active') === 'active');
  const datedGoals  = activeGoals.filter((g) => g.target_date);
  let c_goals;
  if (activeGoals.length >= 2 && datedGoals.length >= 1) c_goals = 10;
  else if (activeGoals.length >= 1)                      c_goals = 5;
  else                                                   c_goals = 0;

  // ---- Component 7: Runway stability (max 10) ----
  let c_runway;
  if (runway >= 6)      c_runway = 10;
  else if (runway >= 3) c_runway = 7;
  else if (runway >= 1) c_runway = 3;
  else                  c_runway = 0;

  // ---- Component 8: Debt awareness (max 5) ----
  // `has_debt` and `emi_amount` don't exist on onboarding_profiles in our
  // current schema (backend gets them as None too). In practice this
  // collapses to: does any liability row exist?
  const has_liab = (liabilities || []).length > 0
    || (holdings || []).some((h) => (h.kind || 'asset') === 'liability');
  const has_emi = Boolean(onb.emi_amount) || Boolean(onb.has_debt);
  const debt_acknowledged = has_liab || has_emi
    || ('has_debt' in onb && onb.has_debt === false);
  const c_debt = debt_acknowledged ? 5 : 0;

  // ---- Component 9: Investment visibility (max 5) ----
  const has_inv_txn = txns.some(isInvestment);
  const has_asset_holding = (holdings || []).some((h) => (h.kind || 'asset') === 'asset');
  const c_invest = (has_inv_txn || has_asset_holding || onbSavings > 0) ? 5 : 0;

  // ---- Weighted sum ----
  let raw = c_coverage + c_income + c_cat + c_savings + c_recurring
          + c_goals + c_runway + c_debt + c_invest;
  raw = clamp(raw, 0, 100);

  // ---- ±7pt smoothing (mirrors backend) ----
  let smoothed = raw;
  if (prevScore != null && Number.isFinite(Number(prevScore))) {
    const prev = Number(prevScore);
    const delta = raw - prev;
    if (Math.abs(delta) > 7) smoothed = prev + (delta > 0 ? 7 : -7);
  }
  const overall = Math.round(smoothed);

  const components = [
    { key: 'transaction_coverage',   label: 'Transaction coverage',   value: c_coverage,  max: 20 },
    { key: 'income_visibility',      label: 'Income visibility',      value: c_income,    max: 15 },
    { key: 'expense_categorization', label: 'Expense categorization', value: c_cat,       max: 10 },
    { key: 'savings_consistency',    label: 'Savings consistency',    value: c_savings,   max: 15 },
    { key: 'recurring_awareness',    label: 'Recurring awareness',    value: c_recurring, max: 10 },
    { key: 'goal_intentionality',    label: 'Goal intentionality',    value: c_goals,     max: 10 },
    { key: 'runway_stability',       label: 'Runway stability',       value: c_runway,    max: 10 },
    { key: 'debt_awareness',         label: 'Debt awareness',         value: c_debt,      max: 5  },
    { key: 'investment_visibility',  label: 'Investment visibility',  value: c_invest,    max: 5  },
  ];

  // ---- Display buckets (Figma 4-dimension visual) ----
  // We normalize each bucket's raw earned points to 0-100 for the progress
  // bars. The `overall` score is still the true 9-component sum.
  const bucketDefs = [
    {
      label: 'Coverage & Data',
      color: '#3E6A80',
      keys: ['transaction_coverage', 'expense_categorization'],
    },
    {
      label: 'Cash flow',
      color: '#3D7A63',
      keys: ['income_visibility', 'savings_consistency', 'runway_stability'],
    },
    {
      label: 'Commitments & Debt',
      color: '#D4A95D',
      keys: ['recurring_awareness', 'debt_awareness'],
    },
    {
      label: 'Goals & Growth',
      color: '#163A32',
      keys: ['goal_intentionality', 'investment_visibility'],
    },
  ];
  const byKey = Object.fromEntries(components.map((c) => [c.key, c]));
  const buckets = bucketDefs.map((b) => {
    const raw_pts  = b.keys.reduce((s, k) => s + (byKey[k]?.value || 0), 0);
    const max_pts  = b.keys.reduce((s, k) => s + (byKey[k]?.max   || 0), 0);
    const score    = max_pts > 0 ? Math.round((raw_pts / max_pts) * 100) : 0;
    return { label: b.label, color: b.color, score, raw: raw_pts, max: max_pts };
  });

  // ---- Band + delta copy (mirrors previous Insights UX language) ----
  const band = stage === 'none'
    ? 'Building your clarity'
    : overall >= 80 ? 'Strong'
    : overall >= 70 ? 'Good standing'
    : overall >= 55 ? 'Building'
    : 'Needs attention';

  const deltaPts = prevScore != null && Number.isFinite(Number(prevScore))
    ? overall - Math.round(Number(prevScore))
    : 0;

  return {
    overall,
    stage,
    confidence,
    band,
    deltaPts,
    components,
    buckets,
    // extra numbers callers may want for tooltips / debug:
    _internal: {
      income, expenses, savings, savingsRate, runway,
      n_txns, months_active, has_imported_statement,
      days_since_signup, income_months: incomeMonthsSet.size,
    },
  };
}

export default computeClarityScore;
